// pages/product/supplier/form.jsx
import { useState, useEffect } from "react"

import ErrorMessage from "components/forms/error-message"
import lodash from "lodash"
import deepdash from "deepdash"
import { lang } from "config"
import Label from "components/forms/label"
import Description from "components/forms/description"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import SelectDescription from "components/forms/select-description"

import useSupplierRepositories from "repositories/supplier"
// pastikan path ini sesuai dengan lokasi AsyncModelPicker kamu
import AsyncModelPicker from "../wizard/AsyncModalPicker"

export default function Form({ data, errors = {}, onSubmit }) {
    const _ = deepdash(lodash)

    const id = data?.id ?? null

    // list model yang terpilih
    const [selectedModelIds, setSelectedModelIds] = useState([])
    const [selectedModels, setSelectedModels] = useState([])

    // supplier
    const [supplierId, setSupplierId] = useState(data?.supplier_id ?? null)

    // ambil list supplier
    const {
        data: supplierSelection,
        isLoading: isLoadingSupplierSelection,
    } = useSupplierRepositories({
        paginate: false,
    })

    // hydrate data awal (edit)
    useEffect(() => {
        if (!data) return

        // support beberapa bentuk data awal:
        // - data.product_model_ids (array)
        // - data.product_models (array of object)
        // - data.product_model_id + data.product_model (single)
        const initialIds = Array.isArray(data.product_model_ids)
            ? data.product_model_ids
            : data.product_model_id
            ? [data.product_model_id]
            : []

        const initialModels = Array.isArray(data.product_models)
            ? data.product_models
            : data.product_model
            ? [data.product_model]
            : []

        setSelectedModelIds(initialIds)
        setSelectedModels(initialModels)
        setSupplierId(data.supplier_id ?? null)
    }, [data])

    const toggleModel = (m) => {
        if (!m?.id) return

        setSelectedModelIds((prevIds) => {
            const exists = prevIds.includes(m.id)
            if (exists) {
                return prevIds.filter((id) => id !== m.id)
            }
            return [...prevIds, m.id]
        })

        setSelectedModels((prevModels) => {
            const exists = prevModels.some((pm) => pm.id === m.id)
            if (exists) {
                return prevModels.filter((pm) => pm.id !== m.id)
            }
            return [...prevModels, m]
        })
    }

    const handleRemoveModel = (id) => {
        setSelectedModelIds((prev) => prev.filter((x) => x !== id))
        setSelectedModels((prev) => prev.filter((m) => m.id !== id))
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (selectedModelIds.length === 0) {
            toast.error("Silakan pilih minimal satu product model")
            return
        }
        if (!supplierId) {
            toast.error("Silakan pilih supplier terlebih dahulu")
            return
        }

        if (!nProgress.isStarted()) {
            onSubmit({
                id,
                supplier_id: supplierId,
                product_model_ids: selectedModelIds,
                product_models: selectedModels, // optional, berguna untuk audit/log
            })
        } else {
            toast.error("Previous action is loading")
        }
    }

    return (
        <form onSubmit={handleSubmit} className="mt-8 space-y-8">
            <div className="mt-10 sm:mt-0">
                <div className="md:grid md:grid-cols-3 md:gap-6">
                    {/* Kiri: judul & deskripsi */}
                    <div className="md:col-span-1">
                        <div className="px-4 sm:px-0">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">
                                {lang.main_form}
                            </h3>
                            <p className="mt-1 text-sm text-gray-600">
                                {lang.please_fill_the_provided_form_input}
                            </p>
                        </div>
                    </div>

                    {/* Kanan: field form */}
                    <div className="mt-5 md:col-span-2 md:mt-0">
                        <div className="border sm:rounded-xl">
                            <div className="px-4 py-5 sm:p-6">
                                <div className="grid grid-cols-2 gap-4">
                                    {/* Product Model (AsyncModelPicker) */}
                                    <div className="col-span-2">
                                        <Label
                                            htmlFor="product_model"
                                            value="Product Model"
                                        />
                                        <p className="mb-2 text-[11px] text-neutral-500">
                                            Klik model untuk menambah / menghapus
                                            dari daftar terpilih.
                                        </p>
                                        <AsyncModelPicker
                                            // value tidak dipakai di component ini, tapi disertakan
                                            // untuk future enhancement kalau mau highlight selection
                                            value={null}
                                            onChange={(m) => toggleModel(m)}
                                        />
                                        <ErrorMessage
                                            error={
                                                errors.product_model_ids ||
                                                errors.product_model_id
                                            }
                                        />

                                        {/* Daftar model terpilih */}
                                        {selectedModels.length > 0 && (
                                            <div className="mt-3 space-y-1">
                                                <p className="text-[11px] font-medium text-neutral-700">
                                                    Model terpilih:
                                                </p>
                                                <div className="flex flex-wrap gap-2">
                                                    {selectedModels.map((m) => (
                                                        <div
                                                            key={m.id}
                                                            className="inline-flex items-center px-2 py-1 text-[11px] bg-neutral-100 rounded-full border border-neutral-300"
                                                        >
                                                            <span className="mr-1 font-medium">
                                                                {m.name}
                                                            </span>
                                                            {m.long_name && (
                                                                <span className="text-neutral-500">
                                                                    — {m.long_name}
                                                                </span>
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleRemoveModel(
                                                                        m.id
                                                                    )
                                                                }
                                                                className="ml-2 text-[10px] text-neutral-500 hover:text-red-500"
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Supplier */}
                                    <div>
                                        <Label
                                            htmlFor="supplier_id"
                                            value="Supplier"
                                        />
                                        <SelectDescription
                                            onChange={(value) =>
                                                setSupplierId(value)
                                            }
                                            selection={supplierSelection || []}
                                            isLoading={isLoadingSupplierSelection}
                                            value={supplierId}
                                            keyValue={(value) => value.id}
                                            title={(value) => value.name}
                                            placeholder="Select Supplier"
                                            disabled={id}
                                        />
                                        <ErrorMessage
                                            error={errors.supplier_id}
                                        />
                                        <Description
                                            error={errors.supplier_id}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tombol submit */}
            <div className="mt-8 text-xs text-right">
                <button
                    type="submit"
                    className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90"
                >
                    <span>
                        {`${_.isEmpty(data) ? lang.create : lang.update}`}
                    </span>
                </button>
            </div>
        </form>
    )
}
